<template>
  <section
    class="invoice-preview-wrapper"
    aria-label="Détails du bon de transfert"
    role="main"
  >

    <div
      v-if="isLoading === true"
      class="loading-container d-print-none"
      aria-live="polite"
      aria-busy="true"
    >
      <b-spinner
        variant="primary"
        class="loading-spinner"
      />
      <p class="mt-3 text-muted">
        Chargement du bon de transfert...
      </p>
    </div>

    <!-- Main Content -->
    <div v-if="transferVoucher">
      <!-- Hero / action bar -->
      <div class="hero-card d-print-none">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div class="hero-left">
            <div class="hero-eyebrow d-flex align-items-center">
              <b-icon
                icon="boxes"
                class="mr-2 text-primary"
              />
              Logistic
            </div>
            <div class="hero-title">
              Bon transfert N°
              <span class="hero-number">{{ transferVoucher.id }}</span>
            </div>
            <div class="hero-meta">
              <div class="meta-item">
                <b-icon
                  icon="calendar3"
                  class="mr-1"
                />
                {{ formatFn(transferVoucher.created_at) }}
              </div>
              <div class="meta-item">
                <b-icon
                  icon="geo-alt-fill"
                  class="mr-1"
                />
                {{ (transferVoucher.from_repository && transferVoucher.from_repository.name) || 'N/A' }}
                <b-icon
                  icon="arrow-right"
                  class="mx-2 text-primary"
                  font-scale="1.1"
                />
                {{ (transferVoucher.to_repository && transferVoucher.to_repository.name) || 'N/A' }}
              </div>
            </div>
            <div class="hero-metrics">
              <span class="metric-pill pill-green">
                <b-icon
                  icon="box-seam"
                  class="mr-1"
                />
                {{ orders.length }} Colis
              </span>
              <span class="metric-pill pill-amber">
                <b-icon
                  icon="arrow-left-right"
                  class="mr-1"
                />
                {{ exchangeCount }} Échanges
              </span>
              <span class="metric-pill pill-dark">
                <b-icon
                  icon="currency-dollar"
                  class="mr-1"
                />
                {{ (transferVoucher.total || 0).toFixed(3) }} DT
              </span>
            </div>
          </div>
          <div class="hero-actions d-flex align-items-center">
            <b-badge
              v-if="transferVoucher.status_display"
              variant="success"
              class="status-chip mr-2"
            >
              {{ transferVoucher.status_display }}
            </b-badge>
            <b-dropdown
              v-ripple.400="'rgba(10, 38, 52, 0.15)'"
              variant="outline-primary"
              class="action-btn-top mr-2 print-btn"
              :disabled="isLoading"
              text="Imprimer"
              right
            >
              <template #button-content>
                <feather-icon
                  icon="PrinterIcon"
                  size="16"
                  class="mr-2"
                />
                Imprimer
                <feather-icon
                  icon="ChevronDownIcon"
                  size="14"
                  class="ml-1"
                />
              </template>

              <b-dropdown-item
                v-b-tooltip.hover.right="'Imprimer une version simplifiée en noir et blanc'"
                @click="printTransferVoucherBW"
              >
                <feather-icon
                  icon="PrinterIcon"
                  size="16"
                  class="mr-2"
                />
                Version noir et blanc
              </b-dropdown-item>

              <b-dropdown-item
                v-b-tooltip.hover.right="'Imprimer le bon de transfert en couleur avec tous les éléments graphiques.'"
                @click="printTransferVoucher"
              >
                <feather-icon
                  icon="PrinterIcon"
                  size="16"
                  class="mr-2"
                />
                Version couleur
              </b-dropdown-item>
            </b-dropdown>
            <b-button
              variant="outline-secondary"
              class="action-btn-top"
              @click="$router.go(-1)"
            >
              <b-icon
                icon="arrow-left"
                class="mr-1"
              />
              Retour
            </b-button>
          </div>
        </div>
      </div>

      <!-- Voucher Content (Print Area) -->
      <div
        id="voucher-content"
        class="invoice-content"
      >
        <b-card class="invoice-card shadow-sm glass-card">
          <!-- Header Section -->
          <div class="invoice-header ribbon">
            <div class="brand-block">
              <div class="company-logo">
                <img
                  :src="transferLogoSrc"
                  :alt="$branding.logo.alt"
                  class="logo-img"
                >
              </div>
              <div class="brand-text">
                <p class="brand-label mb-1">
                  Logistic
                </p>
                <h3 class="brand-name mb-0">
                  Dasco Shipping
                </h3>
              </div>
            </div>
            <div class="voucher-meta text-md-right">
              <p class="meta-label mb-1">
                Bon transfert N°
              </p>
              <h1 class="voucher-number">
                N° {{ transferVoucher.id }}
              </h1>
              <div class="invoice-date d-flex align-items-center justify-content-md-end">
                <b-icon
                  icon="calendar3"
                  class="mr-1"
                />
                {{ formatFn(transferVoucher.created_at) }}
              </div>
            </div>
          </div>

          <!-- Company & Transport Info -->
          <div class="info-grid">
            <div class="info-panel">
              <div class="panel-header">
                <b-icon
                  icon="house-door"
                  class="mr-2 text-primary"
                />
                Notre Société
              </div>
              <document-company-info
                :company="company"
                header-title=""
                layout="vertical"
              />
            </div>
            <div class="info-panel supplier-panel">
              <div class="panel-header">
                <b-icon
                  icon="truck"
                  class="mr-2 text-primary"
                />
                Fournisseur
              </div>
              <div class="panel-body">
                <div class="info-row">
                  <span class="info-label">De</span>
                  <span class="info-value">{{ (transferVoucher.from_repository && transferVoucher.from_repository.name) || 'N/A' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Vers</span>
                  <span class="info-value">{{ (transferVoucher.to_repository && transferVoucher.to_repository.name) || 'N/A' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Chauffeur</span>
                  <span class="info-value">{{ (transferVoucher.delivery_man && transferVoucher.delivery_man.username) || 'Non assigné' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Véhicule</span>
                  <span class="info-value">{{ (transferVoucher.car && transferVoucher.car.serial_number) || 'Non assigné' }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Table -->
          <div class="summary-section">
            <div class="section-header with-icon">
              <b-icon
                icon="bar-chart"
                class="mr-2"
              />
              Résumé de la facture
            </div>
            <div class="summary-table-container">
              <div class="table-responsive">
                <table class="modern-table summary-table">
                  <thead>
                    <tr>
                      <th>Nombre de colis</th>
                      <th>Colis livrés</th>
                      <th>Colis retour</th>
                      <th>Total COD</th>
                      <th>Frais de livraison</th>
                      <th>Frais</th>
                      <th>Net à payer</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <span class="chip chip-blue">{{ orders.length }}</span>
                      </td>
                      <td>
                        <span class="chip chip-green">{{ orders.length }}</span>
                      </td>
                      <td>
                        <span class="chip chip-amber">{{ exchangeCount }}</span>
                      </td>
                      <td class="font-weight-bold">
                        {{ (transferVoucher.total || 0).toFixed(3) }} DT
                      </td>
                      <td class="font-weight-bold">
                        {{ (transferVoucher.delivery_fees || 0).toFixed(3) }} DT
                      </td>
                      <td class="font-weight-bold highlight-cell">
                        {{ (transferVoucher.fees || 0).toFixed(3) }} DT
                      </td>
                      <td class="font-weight-bold text-primary">
                        {{ (transferVoucher.total || 0).toFixed(3) }} DT
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Orders Table -->
          <div
            v-if="orders.length"
            class="orders-section"
          >
            <div class="section-header with-icon">
              <b-icon
                icon="list-check"
                class="mr-2"
              />
              Colis livrés
              <b-badge
                variant="success"
                class="ml-2"
              >
                {{ orders.length }}
              </b-badge>
            </div>
            <div class="table-container">
              <b-table
                :items="orders"
                :fields="tableFields"
                class="modern-orders-table"
                responsive
                striped
                hover
                small
                :tbody-tr-class="getRowClass"
              >
                <template #cell(BL)="{ item }">
                  <div class="qr-cell">
                    <qrcode-vue
                      :value="item.BL.toString()"
                      :size="35"
                      level="H"
                      class="qr-code"
                    />
                    <div class="ref-info">
                      <span class="ref-number">{{ item.BL }}</span>
                      <div class="badges-container">
                        <b-badge
                          v-if="item.status === 18"
                          variant="warning"
                          class="status-badge"
                        >
                          {{ item.status_display || 'Planification' }}
                        </b-badge>
                        <b-badge
                          v-if="item.is_an_exchange"
                          variant="danger"
                          class="exchange-badge"
                        >
                          Échange
                        </b-badge>
                      </div>
                    </div>
                  </div>
                </template>
                <template #cell(prix)="{ item }">
                  <span class="price-cell font-weight-bold text-success">
                    {{ parseFloat(item.prix).toFixed(3) }} DT
                  </span>
                </template>
                <template #cell(tel)="{ item }">
                  <a
                    :href="`tel:${item.tel}`"
                    class="phone-link"
                  >
                    <b-icon
                      icon="telephone"
                      class="mr-1"
                    />
                    {{ item.tel }}
                  </a>
                </template>
              </b-table>
            </div>
          </div>

          <!-- Signature -->
          <div class="summary-section mt-4">
            <div class="section-header with-icon">
              <b-icon
                icon="pen"
                class="mr-2"
              />
              Signature du Responsable
            </div>
            <div class="signature-box" />
          </div>
        </b-card>
      </div>
    </div>
  </section>
</template>

<script>
import {
  BCard,
  BTable,
  BButton,
  BBadge,
  BSpinner,
  BIcon,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'

import axios from 'axios'
import storeAuth from '@/store/store'
import QrcodeVue from 'qrcode.vue'
import { DocumentCompanyInfo } from '@/components/document'

export default {
  components: {
    BCard,
    BTable,
    BButton,
    BBadge,
    BSpinner,
    BIcon,
    QrcodeVue,
    BDropdown,
    BDropdownItem,
    DocumentCompanyInfo,
  },
  data() {
    return {
      transferVoucher: {},
      orders: [],
      isLoading: true,
      company: {},
      tableFields: [
        {
          key: 'BL',
          label: 'BL',
          thClass: 'text-center border-0',
          tdClass: 'text-center border-0',
          sortable: false,
        },
        {
          key: 'fournisseur',
          label: 'Fournisseur',
          thClass: 'border-0',
          tdClass: 'border-0',
        },
        {
          key: 'destinataire',
          label: 'Destinataire',
          thClass: 'border-0',
          tdClass: 'border-0',
        },
        {
          key: 'gouvernorat',
          label: 'Gouvernorat',
          thClass: 'border-0',
          tdClass: 'border-0',
        },
        {
          key: 'tel',
          label: 'Téléphone',
          thClass: 'border-0',
          tdClass: 'border-0',
        },
        {
          key: 'prix',
          label: 'Prix TTC',
          thClass: 'text-right border-0',
          tdClass: 'text-right border-0',
        },
      ],
    }
  },

  computed: {
    exchangeCount() {
      return this.orders.filter(order => order.is_an_exchange).length
    },
    transferLogoSrc() {
      return this.$branding?.logo?.main || '/images/logo/logo.png'
    },
  },

  created() {
    this.id = this.$route.params.id
    this.company = storeAuth.state.company
    this.getTransferVoucher()
  },

  methods: {
    formatFn(date) {
      if (!date) return 'N/A'
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const time = date.slice(11, 19)
      return `${day1}/${month}/${year} à ${time}`
    },

    async getTransferVoucher() {
      try {
        this.isLoading = true
        const response = await axios.get(`/api/transfer-vouchers/detail/${this.$route.params.id}`)
        this.transferVoucher = response.data

        if (this.transferVoucher && this.transferVoucher.orders) {
          this.orders = this.transferVoucher.orders.map(order => ({
            BL: order.id,
            gouvernorat: (order.governorate && order.governorate.name) || 'N/A',
            fournisseur: (order.supplier && order.supplier.username) || 'N/A',
            tel: order.phone1 || 'N/A',
            destinataire: order.name || 'N/A',
            adresse: order.address || 'N/A',
            prix: order.price_ttc || 0,
            is_an_exchange: order.is_an_exchange,
            status: order.status,
            status_display: order.status_display,
          }))
        }
      } catch (error) {
        console.error('Erreur lors du chargement:', error)
        this.transferVoucher = undefined
      } finally {
        this.isLoading = false
      }
    },

    getRowClass(item) {
      return item.status === 18 ? 'table-warning-custom' : ''
    },

    printTransferVoucher() {
      // Dynamic print CSS injection (same as invoices/Show.vue)
      const printCSS = `
        <style id="print-styles-transfer-voucher">
          @media print {
            html, body, #app {
              margin: 0 !important;
              padding: 0 !important;
              font-family: 'Helvetica Neue', Arial, sans-serif;
              position: static !important;
              width: 100% !important;
              overflow: visible !important;
              background: #f4f6f9 !important;
              box-sizing: border-box !important;
            }
            .invoice-preview-wrapper, .transfer-voucher-wrapper, section.invoice-preview-wrapper {
              position: static !important;
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              background: transparent !important;
              box-sizing: border-box !important;
            }
            body * { visibility: hidden; }
            #voucher-content, #voucher-content * { visibility: visible; }
            #voucher-content {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              right: 0 !important;
              margin: 0 auto !important;
              width: 95% !important;
              font-family: 'Helvetica Neue', Arial, sans-serif;
              font-size: 13px;
              line-height: 1.4;
              color: #12212f;
              background: white !important;
              z-index: 9999 !important;
              user-select: none !important;
              cursor: default !important;
              padding: 18px 18px 8px 18px;
              box-shadow: none !important;
            }
            .invoice-card {
              border: 1px solid #d6d9e0 !important;
              box-shadow: none !important;
              border-radius: 10px !important;
              overflow: hidden !important;
            }
            .d-print-none, .feather-icon, .no-print { display: none !important; }
            .invoice-header {
              margin-bottom: 14px;
              padding: 16px 18px;
              background: #0A2634 !important;
              color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              page-break-inside: avoid;
              border-bottom: 1px solid #0a1a23;
            }
            .logo-img {
              max-width: 120px !important;
              height: auto !important;
              filter: brightness(0) invert(1) !important;
            }
            .brand-label, .meta-label { color: #c5d2dc !important; font-size: 12px !important; }
            .brand-name { color: #ffffff !important; font-weight: 800 !important; }
            .voucher-number { color: #ff4f4f !important; }
            .invoice-date { color: #c5d2dc !important; font-size: 12px !important; }

            .info-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
              gap: 10px;
              margin-bottom: 12px;
            }
            .info-panel {
              border: 1px solid #d6d9e0 !important;
              background: #fff !important;
              border-radius: 8px !important;
              padding: 12px !important;
              page-break-inside: avoid;
              box-shadow: none !important;
            }
            .panel-header {
              font-weight: 700 !important;
              color: #0a2634 !important;
              margin-bottom: 8px !important;
            }
            .panel-body, .info-row {
              font-size: 12px !important;
              color: #12212f !important;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              padding: 4px 0;
              border-bottom: 1px solid #ebeff3;
            }
            .info-row:last-child { border-bottom: none; }
            .info-label { font-weight: 700; color: #4a5568; }
            .info-value { font-weight: 700; color: #12212f; text-align: right; }

            .summary-section, .orders-section { page-break-inside: avoid; }
            .summary-table-container, .table-container {
              border: 1px solid #d6d9e0 !important;
              background: #fff !important;
              margin-bottom: 12px !important;
              box-shadow: none !important;
              border-radius: 6px !important;
            }
            .modern-table, .modern-orders-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 12px;
            }
            .modern-table th, .modern-orders-table th {
              background: #0A2634 !important;
              color: #fff !important;
              padding: 8px 6px;
              border: 1px solid #cfd8e3;
              font-weight: 700;
              text-align: center;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .modern-table td, .modern-orders-table td {
              padding: 7px 6px;
              border: 1px solid #cfd8e3;
              color: #12212f !important;
              font-size: 12px;
              text-align: center;
            }
            .modern-orders-table th { text-align: left; }
            .modern-orders-table td { text-align: left; }

            .chip { color: #12212f !important; background: #eaf1f9 !important; border-radius: 999px; padding: 4px 8px; }
            .highlight-cell { background: #fff7d6 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }

            .summary-section .section-header,
            .orders-section .section-header {
              padding: 6px 8px;
              border-radius: 6px;
              border: 1px solid #d6d9e0;
              background: #f8fafc;
              font-weight: 700;
              color: #12212f;
              page-break-after: avoid;
            }

            .signature-box {
              border: 1px solid #d6d9e0 !important;
              height: 60px;
              background: #fff !important;
            }

            .ml-2, .badge:not(.stat-badge) {
              display: none !important;
            }
            .section-header {
              page-break-after: avoid;
            }
            * {
              transition: none !important;
              animation: none !important;
              transform: none !important;
            }
          }
        </style>
      `
      document.head.insertAdjacentHTML('beforeend', printCSS)
      window.print()
      setTimeout(() => {
        const printStyleElement = document.getElementById('print-styles-transfer-voucher')
        if (printStyleElement) {
          printStyleElement.remove()
        }
      }, 1000)
    },
    printTransferVoucherBW() {
      // Ultra-optimized print version: no backgrounds, only black text, minimal borders, visible links, hide scrollbars
      const printCSS = `
        <style id="print-styles-transfer-voucher-bw">
          @media print {
            html, body, #app {
              margin: 0 !important;
              padding: 0 !important;
              font-family: Arial, sans-serif;
              position: static !important;
              width: 100% !important;
              overflow: visible !important;
              background: #fff !important;
              color: #000 !important;
              box-sizing: border-box !important;
            }
            .invoice-preview-wrapper, .transfer-voucher-wrapper, section.invoice-preview-wrapper {
              position: static !important;
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              background: #fff !important;
              color: #000 !important;
              box-sizing: border-box !important;
            }
            /* Hide scrollbars for all elements in print */
            *::-webkit-scrollbar {
              display: none !important;
              width: 0 !important;
              background: transparent !important;
            }
            * {
              scrollbar-width: none !important;
              -ms-overflow-style: none !important;
            }
            body * { visibility: hidden; }
            #voucher-content, #voucher-content * { visibility: visible; }
            #voucher-content {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              right: 0 !important;
              margin: 0 !important;
              width: 100% !important;
              font-family: Arial, sans-serif;
              font-size: 14px;
              line-height: 1.4;
              color: #000 !important;
              background: #fff !important;
              z-index: 9999 !important;
              user-select: none !important;
              cursor: default !important;
            }
            .modern-table, .modern-orders-table {
              user-select: none !important;
              cursor: default !important;
              background: #fff !important;
              color: #000 !important;
              border: 1px solid #bbb !important;
            }
            .modern-table th, .modern-orders-table th {
              background: none !important;
              color: #000 !important;
              border: 1px solid #bbb !important;
              font-weight: bold;
              font-size: 14px;
              text-align: center;
              padding: 8px 6px;
            }
            .modern-table td, .modern-orders-table td {
              background: none !important;
              color: #000 !important;
              border: 1px solid #bbb !important;
              font-size: 14px;
              text-align: center;
              padding: 6px 4px;
            }
            .d-print-none, .feather-icon, .no-print { display: none !important; }
            .invoice-header {
              margin-bottom: 20px;
              padding: 20px 0 10px 0;
              background: none !important;
              color: #000 !important;
              border-bottom: 2px solid #bbb !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              page-break-inside: avoid;
            }
            .logo-img {
              max-width: 120px !important;
              height: auto !important;
              filter: none !important;
            }
            .invoice-title {
              font-size: 24px;
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 8px;
            }
            .invoice-number {
              color: #000 !important;
            }
            .invoice-date {
              font-size: 14px;
              color: #000 !important;
            }
            .section-title {
              font-size: 18px;
              font-weight: bold;
              border-bottom: 2px solid #bbb !important;
              margin: 15px 0 10px 0;
              padding-bottom: 5px;
              color: #000 !important;
              page-break-after: avoid;
            }
            .row { display: flex; flex-wrap: wrap; margin: 0; }
            .col-md-6 { flex: 0 0 50%; max-width: 50%; padding: 0 5px; }
            .info-card, .supplier-card {
              border: 1px solid #bbb !important;
              padding: 15px;
              margin-bottom: 15px;
              background: none !important;
              border-left: none !important;
              min-height: 120px;
              page-break-inside: avoid;
              color: #000 !important;
            }
            .info-header {
              font-size: 14px;
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 10px;
              border-bottom: 1px solid #bbb !important;
              padding-bottom: 5px;
            }
            .info-item {
              margin-bottom: 8px;
              font-size: 14px;
              color: #000 !important;
            }
            .info-label {
              font-weight: bold;
              color: #000 !important;
              min-width: 100px;
              display: inline-block;
              font-size: 14px;
            }
            .info-subtitle {
              font-style: italic;
              color: #555 !important;
              font-size: 13px;
            }
            .summary-table-container, .table-container {
              border: 1px solid #bbb !important;
              margin-bottom: 20px;
              background: none !important;
              color: #000 !important;
            }
            .stat-badge, .amount, .total-amount {
              background: none !important;
              color: #000 !important;
              border: none !important;
              font-weight: bold;
            }
            .signature-section, .company-info-section {
              page-break-inside: avoid;
            }
            .table-header, .voucher-header, .card-header-custom, .signature-header {
              page-break-after: avoid !important;
            }
            .reason-item {
              page-break-inside: avoid !important;
            }
            .signature-title {
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 15px;
              font-size: 12px;
              text-align: center;
            }
            .signature-line {
              height: 60px;
              border-bottom: 2px solid #000 !important;
              margin-bottom: 5px;
              background: #fff !important;
            }
            .ml-2, .badge:not(.stat-badge) {
              display: none !important;
            }
            .section-header {
              page-break-after: avoid;
            }
            a, .phone-link {
              color: #000 !important;
              text-decoration: underline !important;
              font-weight: bold !important;
              word-break: break-all;
            }
            a[href]:after, .phone-link[href]:after {
              content: " (" attr(href) ")";
              font-size: 11px;
              color: #555;
            }
            * {
              transition: none !important;
              animation: none !important;
              transform: none !important;
            }
          }
        </style>
      `
      document.head.insertAdjacentHTML('beforeend', printCSS)
      window.print()
      setTimeout(() => {
        const printStyleElement = document.getElementById('print-styles-transfer-voucher-bw')
        if (printStyleElement) {
          printStyleElement.remove()
        }
      }, 1000)
    },
  },
}
</script>

<style lang="scss" scoped>
// Brand palette tuned to the provided mock
$ink: #1f2d3d;
$muted: #7b8794;
$accent-red: #d62828;
$accent-green: #2f9e44;
$accent-amber: #f59f00;
$accent-dark: #0a2634;
$soft-bg: #f5f7fb;
$soft-card: #f7fafc;
$soft-border: #e7ebf0;

.invoice-preview-wrapper {
  padding: 1.5rem;
  background: linear-gradient(180deg, #f7f9fc 0%, #eef2f7 100%);
  min-height: 100vh;
  font-size: 16px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.loading-spinner {
  width: 3rem;
  height: 3rem;
}

.invoice-content {
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
}

.hero-card {
  background: #fff;
  border-radius: 18px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid $soft-border;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(47, 158, 68, 0.08), rgba(245, 159, 0, 0.08));
    opacity: 0.6;
    z-index: 0;
  }

  > * { position: relative; z-index: 1; }
}

.hero-eyebrow {
  color: $ink;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  font-size: 0.85rem;
  margin-bottom: 0.35rem;
}

.hero-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: $ink;
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
}

.hero-number {
  color: $accent-red;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem 1.25rem;
  margin-top: 0.4rem;

  .meta-item {
    display: inline-flex;
    align-items: center;
    color: $muted;
    font-weight: 600;
    font-size: 0.95rem;
  }
}

.hero-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.85rem;
}

.metric-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.9rem;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.06);

  &.pill-green {
    background: rgba($accent-green, 0.12);
    color: $accent-green;
  }
  &.pill-amber {
    background: rgba($accent-amber, 0.14);
    color: $accent-amber;
  }
  &.pill-dark {
    background: rgba($accent-dark, 0.1);
    color: $accent-dark;
  }
}

.hero-actions {
  gap: 0.5rem;
}

.status-chip {
  padding: 0.45rem 0.85rem;
  border-radius: 10px;
  font-weight: 700;
  background: rgba($accent-green, 0.12);
  color: $accent-green;
}

.action-btn-top {
  border-radius: 10px;
  font-weight: 700;
  padding: 0.55rem 1.1rem;
  transition: all 0.2s ease;
  border-color: $accent-dark;
  color: $accent-dark;

  &:hover {
    background-color: rgba($accent-dark, 0.08);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }
}

.print-btn {
  border-color: $accent-red;
  color: $accent-red;

  &:hover {
    background: rgba($accent-red, 0.08);
    border-color: $accent-red;
    color: $accent-red;
  }
}

.invoice-card {
  border-radius: 18px;
  border: 1px solid $soft-border;
  overflow: hidden;
}

.glass-card {
  background: #fff;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.08);
}

.invoice-header {
  padding: 1.4rem;
  background: linear-gradient(135deg, #ffe9e9 0%, #fff4f4 100%);
  border-radius: 14px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  border: 1px solid #ffd8d8;
  box-shadow: 0 8px 24px rgba(214, 40, 40, 0.08);
}

.brand-block {
  display: inline-flex;
  align-items: center;
  gap: 0.9rem;
}

.company-logo {
  background: transparent;
  padding: 0;
  border-radius: 10px;
  border: none;
  box-shadow: none;

  .logo-img {
    max-width: 170px;
    height: auto;
    display: block;
  }
}

.brand-text {
  .brand-label {
    font-weight: 700;
    color: $muted;
    text-transform: uppercase;
    font-size: 0.85rem;
    letter-spacing: 0.4px;
  }
  .brand-name {
    font-weight: 800;
    color: $accent-dark;
  }
}

.voucher-meta {
  min-width: 260px;
  .meta-label {
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.3px;
    color: $muted;
    font-size: 0.9rem;
  }
  .voucher-number {
    font-size: 1.8rem;
    font-weight: 800;
    color: $accent-red;
    margin: 0.15rem 0;
  }
  .invoice-date {
    color: $ink;
    font-weight: 600;
  }
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.info-panel {
  background: $soft-card;
  border: 1px solid $soft-border;
  border-radius: 14px;
  padding: 1.1rem 1.25rem;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.05);
}

.supplier-panel {
  background: #fff;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 800;
  color: $ink;
  margin-bottom: 0.6rem;
}

.panel-body {
  display: grid;
  gap: 0.35rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
  border-bottom: 1px solid $soft-border;

  &:last-child { border-bottom: 0; }

  .info-label {
    font-weight: 700;
    color: $muted;
    font-size: 0.95rem;
  }
  .info-value {
    font-weight: 700;
    color: $ink;
    text-align: right;
  }
}

.summary-section, .orders-section {
  margin-bottom: 1.75rem;
}

.section-header {
  font-weight: 800;
  color: $ink;
  margin-bottom: 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;

  &.with-icon {
    background: #fff;
    padding: 0.65rem 0.9rem;
    border-radius: 10px;
    border: 1px solid $soft-border;
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.04);
  }
}

.summary-table-container {
  background: #fff;
  border-radius: 14px;
  border: 1px solid $soft-border;
  overflow: hidden;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.06);
}

.modern-table {
  width: 100%;
  margin-bottom: 0;

  thead {
    background: $accent-dark;
    color: #fff;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.9rem;
    letter-spacing: 0.4px;
  }

  th, td {
    padding: 1rem 0.75rem;
    vertical-align: middle;
    border-color: $soft-border;
    text-align: center;
    font-weight: 700;
  }
}

.summary-table th {
  background: $accent-dark;
}

.highlight-cell {
  background: #fff0c2;
}

.chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-weight: 800;
  color: #fff;
  &.chip-blue { background: #29a3fe; }
  &.chip-green { background: $accent-green; }
  &.chip-amber { background: $accent-amber; color: #fff; }
}

.table-container {
  background: #fff;
  border-radius: 14px;
  border: 1px solid $soft-border;
  overflow: hidden;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.06);
}

.modern-orders-table {
  margin-bottom: 0;

  thead {
    background: #f4f6fb;
    th {
      border-bottom: 1px solid $soft-border;
      font-weight: 800;
      color: $ink;
      text-transform: uppercase;
      font-size: 0.85rem;
    }
  }

  tbody tr:hover {
    background: #f9fbff;
  }
}

.qr-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .qr-code {
    flex-shrink: 0;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }

  .ref-info {
    .ref-number {
      font-weight: 800;
      color: $ink;
      display: block;
    }
    .badges-container {
      margin-top: 0.25rem;

      .status-badge, .exchange-badge {
        font-size: 0.72rem;
        margin-right: 0.25rem;
      }
    }
  }
}

.price-cell {
  font-size: 1.05rem;
}

.phone-link {
  color: $accent-dark;
  text-decoration: none;

  &:hover { text-decoration: underline; }
}

.signature-box {
  height: 90px;
  border: 2px dashed $soft-border;
  border-radius: 10px;
  background: #fdfdfd;
}

// Print styles for transfer voucher
// Unified global print CSS to prevent blank/black page and blank first page
@media print {
  html, body, #app, .invoice-preview-wrapper, .transfer-voucher-wrapper {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    font-size: 12px;
    width: 100% !important;
    position: static !important;
    box-sizing: border-box !important;
  }
  .invoice-preview-wrapper, .transfer-voucher-wrapper {
    position: static !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  main, section, .invoice-content, .invoice-card {
    margin: 0 !important;
    padding: 0 !important;
    position: static !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  .top-action-bar, .d-print-none {
    display: none !important;
  }
  // Hide navigation and non-essential elements
  nav, .main-menu, .header-navbar-shadow,
  .customizer-toggle, footer.footer,
  .actions-card, .loading-container, .error-container {
    display: none !important;
  }
  .content.app-content {
    margin: 0 !important;
    padding: 1rem !important;
  }
  .invoice-card, .card {
    box-shadow: none !important;
    border: 1px solid #dee2e6 !important;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .modern-table, .modern-orders-table {
    font-size: 11px;
    th, td {
      padding: 0.5rem !important;
      border: 1px solid #000 !important;
    }
    tr {
      page-break-inside: avoid !important;
    }
    .qr-cell {
      flex-direction: row;
      align-items: center;
    }
    .qr-code {
      width: 30px !important;
      height: 30px !important;
    }
  }
  // Section titles and headers: keep with content
  .table-header,
  .voucher-header,
  .card-header-custom,
  .signature-header {
    page-break-after: avoid !important;
  }
  // Keep logical sections together
  .info-card,
  .stats-card,
  .signature-card {
    page-break-inside: avoid !important;
  }
  // Color adjustments for print
  .bg-primary, .text-primary { color: #000 !important; }
  .bg-success, .text-success { color: #000 !important; }
  .bg-warning, .text-warning { color: #000 !important; }
  // Signature box for print
  .signature-box {
    height: 60px !important;
    border: 2px solid #000 !important;
    background: white !important;
  }
  // Reason items: don't split
  .reason-item {
    page-break-inside: avoid !important;
  }
}
</style>

<style lang="scss">
// Print Styles
@media print {
  html, body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    font-size: 12px;
  }

  .transfer-voucher-wrapper {
    background: white !important;
    padding: 0 !important;
  }

  // Hide navigation and non-essential elements
  nav, .main-menu, .header-navbar-shadow,
  .customizer-toggle, footer.footer,
  .actions-card, .loading-container, .error-container {
    display: none !important;
  }

  .content.app-content {
    margin: 0 !important;
    padding: 1rem !important;
  }

  // Ensure cards print properly
  .card {
    box-shadow: none !important;
    border: 1px solid #dee2e6 !important;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  // Table print optimization
  .modern-table {
    font-size: 11px;

    th, td {
      padding: 0.5rem !important;
      border: 1px solid #000 !important;
    }

    // Remove page-break-inside: avoid from table container
    // Add page-break-inside: avoid to table rows
    tr {
      page-break-inside: avoid !important;
    }

    .qr-cell {
      flex-direction: row;
      align-items: center;
    }

    .qr-code {
      width: 30px !important;
      height: 30px !important;
    }
  }

  // Section titles and headers: keep with content
  .table-header,
  .voucher-header,
  .card-header-custom,
  .signature-header {
    page-break-after: avoid !important;
  }

  // Keep logical sections together
  .info-card,
  .stats-card,
  .signature-card {
    page-break-inside: avoid !important;
  }

  // Color adjustments for print
  .bg-primary, .text-primary { color: #000 !important; }
  .bg-success, .text-success { color: #000 !important; }
  .bg-warning, .text-warning { color: #000 !important; }

  // Signature box for print
  .signature-box {
    height: 60px !important;
    border: 2px solid #000 !important;
    background: white !important;
  }

  // Reason items: don't split
  .reason-item {
    page-break-inside: avoid !important;
  }
}
</style>
